import React from 'react';
import { cn } from '@/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface StatCardProps {
  label: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon?: React.ReactNode;
  className?: string;
  format?: 'currency' | 'percent' | 'number';
}

export function StatCard({ label, value, change, changeLabel, icon, className, format }: StatCardProps) {
  const isPositive = change !== undefined && change > 0;
  const isNegative = change !== undefined && change < 0;

  return (
    <div className={cn('stat-card', className)}>
      <div className="flex items-center justify-between">
        <span className="stat-label">{label}</span>
        {icon && <span className="text-surface-500">{icon}</span>}
      </div>
      <span className="stat-value">{value}</span>
      {change !== undefined && (
        <div className="flex items-center gap-1">
          {isPositive ? (
            <TrendingUp className="w-3 h-3 text-profit" />
          ) : isNegative ? (
            <TrendingDown className="w-3 h-3 text-loss" />
          ) : (
            <Minus className="w-3 h-3 text-surface-500" />
          )}
          <span className={cn('text-xs font-medium', isPositive ? 'text-profit' : isNegative ? 'text-loss' : 'text-surface-500')}>
            {isPositive ? '+' : ''}{change.toFixed(2)}%
          </span>
          {changeLabel && (
            <span className="text-xs text-surface-600">{changeLabel}</span>
          )}
        </div>
      )}
    </div>
  );
}

interface CardProps {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  noPadding?: boolean;
}

export function Card({ title, subtitle, action, children, className, noPadding }: CardProps) {
  return (
    <div className={cn('card', className)}>
      {(title || action) && (
        <div className="card-header">
          <div>
            {title && <h3 className="card-title">{title}</h3>}
            {subtitle && <p className="text-xs text-surface-500 mt-0.5">{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      {noPadding ? <div className="-m-5">{children}</div> : children}
    </div>
  );
}

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}

export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      {icon && <div className="text-surface-600 mb-3">{icon}</div>}
      <h3 className="text-sm font-medium text-surface-300">{title}</h3>
      {description && <p className="text-xs text-surface-500 mt-1 max-w-sm">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
}

export function LoadingSpinner({ size = 'md', text }: LoadingSpinnerProps) {
  const sizeClasses = { sm: 'w-4 h-4', md: 'w-8 h-8', lg: 'w-12 h-12' };

  return (
    <div className="flex flex-col items-center justify-center gap-3 py-8">
      <div
        className={cn(
          'border-2 border-brand-500 border-t-transparent rounded-full animate-spin',
          sizeClasses[size],
        )}
      />
      {text && <span className="text-sm text-surface-400">{text}</span>}
    </div>
  );
}

interface BadgeProps {
  variant?: 'profit' | 'loss' | 'neutral' | 'warning' | 'brand';
  children: React.ReactNode;
  className?: string;
}

export function Badge({ variant = 'neutral', children, className }: BadgeProps) {
  const variantClasses = {
    profit: 'badge-profit',
    loss: 'badge-loss',
    neutral: 'badge-neutral',
    warning: 'badge-warning',
    brand: 'badge-brand',
  };

  return (
    <span className={cn(variantClasses[variant], className)}>
      {children}
    </span>
  );
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

export function Modal({ isOpen, onClose, title, children, size = 'md' }: ModalProps) {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={cn('relative bg-surface-900 border border-surface-800 rounded-xl shadow-2xl w-full animate-in', sizeClasses[size])}>
        <div className="flex items-center justify-between p-5 border-b border-surface-800">
          <h2 className="text-lg font-semibold text-surface-100">{title}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-surface-500 hover:text-surface-300 hover:bg-surface-800"
          >
            ✕
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
